import React, { useMemo } from 'react';
import {
  Reply,
  Forward,
  Archive,
  Calendar,
  Link2,
  Eye,
  Paperclip,
  UtensilsCrossed,
  Plus,
} from 'lucide-react';

/**
 * Email Reading Pane Component
 * Displays full email content with actions
 */
const EmailReadingPane = ({
  email,
  events = [],
  tableBookings = [],
  onReply = () => {},
  onForward = () => {},
  onCreateEvent = () => {},
  onCreateBooking = () => {},
  onLinkToEvent = () => {},
  onLinkToBooking = () => {},
  onArchive = () => {},
  onSelectEvent = () => {},
}) => {
  // Find the linked event or booking, if any. A single email can be linked
  // to either (or in edge cases, both).
  const linkedEvent = useMemo(() => {
    if (!email) return null;
    const id = email.processedEventId || email.eventId || email.linkedEventId;
    return id ? events.find((e) => e.id === id) : null;
  }, [events, email]);
  const linkedBooking = useMemo(() => {
    if (!email) return null;
    const id = email.processedBookingId || email.bookingId;
    return id ? tableBookings.find((b) => b.id === id) : null;
  }, [tableBookings, email]);

  if (!email) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
        <p className="font-medium">No email selected</p>
      </div>
    );
  }

  // Format date with full details
  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-IE', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Parse body as HTML or text
  const renderBody = () => {
    const body = email.body || email.bodyText || email.body_text || '';

    // If body contains HTML tags, use dangerouslySetInnerHTML
    if (body.includes('<') && body.includes('>')) {
      return (
        <div
          className="prose prose-sm max-w-none text-gray-700 break-words"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      );
    }

    // Plain text: split by newlines and render as paragraphs
    return (
      <div className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
        {body}
      </div>
    );
  };

  const direction = email.direction || 'inbound';
  const isSent = direction === 'outbound';

  // Normalise sender/recipient across every variant the rest of the app uses.
  // Inbound DB rows (inbound_emails) store from_address / from_name / to_address;
  // the app-side mappers expose them as from / fromName / to. Outbound rows
  // (sent_emails) store recipient, which the mapper exposes as `to`. A few
  // older call sites still use senderEmail / recipientEmail so we fall back
  // to those last so nothing renders "undefined".
  //
  // Legacy rows written before the fetch-emails Edge Function fix can contain
  // the literal string "undefined@undefined" as an address — scrub those so
  // the UI shows "— unknown sender —" instead of that ugly value.
  const sanitizeAddress = (s) => {
    if (!s) return '';
    const t = String(s).trim();
    if (!t || t === 'undefined' || t === 'undefined@undefined' || t === 'unknown') return '';
    return t;
  };
  const senderEmail = sanitizeAddress(
    email.from || email.fromAddress || email.from_address || email.senderEmail || ''
  );
  const senderName = sanitizeAddress(email.fromName || email.from_name || email.senderName)
    || (senderEmail ? senderEmail.split('@')[0] : '')
    || '— unknown sender —';
  const recipientEmail = sanitizeAddress(
    email.to || email.toAddress || email.to_address || email.recipientEmail || ''
  );
  const recipientName = sanitizeAddress(email.recipientName) || recipientEmail || '';

  const primaryAddress = isSent ? recipientEmail : senderEmail;
  const primaryName = isSent ? (recipientName || recipientEmail) : senderName;

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {email.subject || '(no subject)'}
        </h2>

        {/* From/To and Date */}
        <div className="space-y-3 text-sm">
          <div>
            <span className="text-gray-600">
              {isSent ? 'To:' : 'From:'}
            </span>
            <span className="ml-2 font-medium text-gray-900">
              {primaryName || '—'}
            </span>
            {primaryAddress && primaryAddress !== primaryName && (
              <span className="ml-2 text-gray-500">
                &lt;{primaryAddress}&gt;
              </span>
            )}
          </div>
          {!isSent && recipientEmail && (
            <div>
              <span className="text-gray-600">To:</span>
              <span className="ml-2 font-medium text-gray-900">
                {recipientEmail}
              </span>
            </div>
          )}
          <div>
            <span className="text-gray-600">Date:</span>
            <span className="ml-2 text-gray-900">
              {formatFullDate(email.date || email.sentAt)}
            </span>
          </div>
        </div>

        {/* Linked item badges — event, booking, or both */}
        <div className="mt-4 flex flex-wrap gap-2">
          {linkedEvent && (
            <button
              onClick={() => onSelectEvent?.(linkedEvent)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
              title="Jump to linked event"
            >
              <Calendar size={16} className="text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">
                {linkedEvent.eventName || linkedEvent.name}
              </span>
            </button>
          )}
          {linkedBooking && (
            <span className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
              <UtensilsCrossed size={16} className="text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">
                {linkedBooking.guestName || 'Table booking'}
                {linkedBooking.date ? ` · ${linkedBooking.date}` : ''}
                {linkedBooking.time ? ` ${linkedBooking.time}` : ''}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl">
          {renderBody()}
        </div>
      </div>

      {/* Attachments (if any) */}
      {email.attachments && email.attachments.length > 0 && (
        <div className="border-t border-gray-200 p-6">
          <h3 className="flex items-center gap-2 font-medium text-gray-900 mb-3">
            <Paperclip size={18} /> Attachments
          </h3>
          <div className="space-y-2">
            {email.attachments.map((file, idx) => (
              <a
                key={idx}
                href={file.url}
                download={file.name}
                className="block p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors text-sm text-gray-700"
              >
                📄 {file.name}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Tracking Info (for sent emails) */}
      {isSent && email.tracking && (
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Eye size={16} className="text-gray-600" />
              <span className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">
                  {email.tracking.opens || 0}
                </span>{' '}
                opens
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Link2 size={16} className="text-gray-600" />
              <span className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">
                  {email.tracking.clicks || 0}
                </span>{' '}
                clicks
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="border-t border-gray-200 p-6 flex gap-2 flex-wrap">
        <button
          onClick={onReply}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        >
          <Reply size={16} /> Reply
        </button>
        <button
          onClick={onForward}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Forward size={16} /> Forward
        </button>
        <div className="h-6 w-px bg-gray-200 mx-1 self-center" />
        <button
          onClick={onCreateEvent}
          title="Create a new event pre-filled from this email"
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Plus size={16} /> New Event
        </button>
        <button
          onClick={onCreateBooking}
          title="Create a new table booking pre-filled from this email"
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Plus size={16} /> New Booking
        </button>
        <button
          onClick={onLinkToEvent}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Link2 size={16} /> {linkedEvent ? 'Change Event' : 'Link to Event'}
        </button>
        <button
          onClick={onLinkToBooking}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Link2 size={16} /> {linkedBooking ? 'Change Booking' : 'Link to Booking'}
        </button>
        <div className="h-6 w-px bg-gray-200 mx-1 self-center" />
        <button
          onClick={onArchive}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Archive size={16} /> Archive
        </button>
      </div>
    </div>
  );
};

export default EmailReadingPane;
